/**
 * MA-LP-THEME-036: Single Source of Theme Resolution
 * ──────────────────────────────────────────────────
 * This module is the CANONICAL entry point for theme resolution
 * across ALL rendering surfaces (LP Builder, PPB LP Step, PPB
 * Funnel Step, ConversationFunnel in LP).
 *
 * Architecture:
 *   1. extractLPTheme(lp, version) → canonical theme input
 *   2. resolveLPBrandTokens(theme) → full LP token set (existing)
 *   3. deriveFunnelColors(lpTokens) → funnel-compatible color map
 *
 * Every surface that renders LP content or funnel content in LP
 * context MUST resolve tokens through this flow. No per-surface
 * defaults, no independent theme resolution.
 *
 * Python mirror: backend/app/brand_tokens.py (for runtime LP)
 * ──────────────────────────────────────────────────────────────
 */

import { resolveLPBrandTokens, DEFAULT_BRAND } from './resolveLPBrandTokens';

// ── Single canonical default ─────────────────────────────────
// Re-exported from resolveLPBrandTokens.DEFAULT_BRAND
// This is the ONLY default. No other file should define its own.
export const DEFAULT_LP_THEME = DEFAULT_BRAND;

/**
 * Extract the canonical theme from LP data.
 * Priority: lp.metadata.brand_config > version.snapshot_json.brand_config > API brand config > DEFAULT
 *
 * @param {Object} opts
 * @param {Object} opts.lp - LP entity
 * @param {Object} opts.version - Current LP version
 * @param {Object} opts.apiBrandConfig - Brand config from /api/brand-config (LP Builder)
 * @returns {{ accent: string, surface_mode: string }}
 */
export function extractLPTheme({ lp = null, version = null, apiBrandConfig = null } = {}) {
  const sources = [
    lp?.metadata?.brand_config,
    version?.snapshot_json?.brand_config,
    apiBrandConfig,
  ];

  for (const src of sources) {
    if (src && (src.accent || src.surface_mode)) {
      return {
        accent: src.accent || DEFAULT_LP_THEME.accent,
        surface_mode: src.surface_mode || DEFAULT_LP_THEME.surface_mode,
      };
    }
  }

  return { ...DEFAULT_LP_THEME };
}

/**
 * Full token resolution from theme input.
 * Delegates to resolveLPBrandTokens (the canonical resolver).
 * Returns the complete LP token set used by all Preview* renderers.
 *
 * @param {{ accent: string, surface_mode: string }} theme
 * @returns {Object} Full LP token set
 */
export function resolveTokens(theme = null) {
  return resolveLPBrandTokens(theme || DEFAULT_LP_THEME);
}

/**
 * Derive funnel-compatible color map from resolved LP tokens.
 * Used by FunnelLivePreview and CompactFunnelPreview when
 * rendering in LP/PPB context.
 *
 * Maps LP section tokens → chat UI color needs:
 *   LP cardBg → funnel containerBg
 *   LP surface1 → funnel threadBg
 *   LP accent → funnel CTA/userColor
 *   etc.
 *
 * @param {Object} lpTokens - Resolved tokens from resolveLPBrandTokens
 * @returns {Object} Funnel color map matching FunnelLivePreview expectations
 */
export function deriveFunnelColors(lpTokens) {
  if (!lpTokens) return null;

  const isDark = lpTokens.surfaceMode === 'dark';

  return {
    // Container / card
    surface: isDark ? '#111827' : '#ffffff',
    background: isDark ? '#0f172a' : '#f9fafb',
    // CTA — from LP accent
    cta: lpTokens.accent,
    cta_text: '#ffffff',
    cta_hover: lpTokens.accentStrong,
    // Text
    text: lpTokens.textPrimary,
    text_muted: lpTokens.textMuted,
    // Border
    border: isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb',
  };
}
